import React, { useState } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LogOut, Mail, Phone, User, Users, Shield, Loader2 } from 'lucide-react';
import { useUserDevice } from '@/hooks/useUserDevice';

export const ProfileAvatar: React.FC = () => {
  const { user, isLoaded: isUserLoaded } = useUser();
  const { signOut } = useClerk();
  const { device, currentUserIdentity, isLoading: isDeviceLoading } = useUserDevice();
  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  // Don't render if not loaded or not signed in
  if (!isUserLoaded || !user) {
    return null;
  }

  // Get user initials
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  const email = user.primaryEmailAddress?.emailAddress || '';
  const phone = user.primaryPhoneNumber?.phoneNumber || '';
  
  const initial = firstName 
    ? firstName.charAt(0).toUpperCase() 
    : email.charAt(0).toUpperCase();

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || email;

  // Use Convex identity as the authoritative source, fallback to Clerk metadata
  const identity = currentUserIdentity || (user.publicMetadata?.role as string) || 'User';
  const identityLower = identity.toLowerCase();
  
  const isMentor = ['mentor', 'parent', 'admin', 'supervisor', 'organization'].includes(identityLower);
  const isSelfManaged = identityLower === 'self-managed' || identityLower === 'selfmanaged';
  const isChild = identityLower === 'child';
  const isMentee = identityLower === 'mentee';

  // Get associated users from device (for mentor/supervisor info)
  const associatedUsers = device?.associatedUsers || [];
  const supervisor = associatedUsers.find(
    (u: any) => ['parent', 'mentor', 'supervisor', 'admin', 'organization'].includes(u.identity?.toLowerCase() || '')
  );

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      // Clear mentee device mode if set
      localStorage.removeItem('mentee_device_mode');
      localStorage.removeItem('device_serial');
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md hover:shadow-lg active:scale-95 transition-all"
        aria-label="Open profile"
      >
        <span className="text-sm font-semibold text-primary-foreground">{initial}</span>
      </button>

      {/* Profile Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-[340px] rounded-2xl p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="sr-only">Profile</DialogTitle>
            
            {/* User Info */}
            <div className="flex items-center gap-4">
              <Avatar className="w-14 h-14 bg-gradient-to-br from-primary to-primary/80">
                <AvatarFallback className="bg-transparent text-primary-foreground text-lg font-semibold">
                  {initial}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-foreground truncate">{fullName}</h2>
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full inline-block mt-1 ${
                  isMentor 
                    ? 'bg-blue-500/10 text-blue-600'
                    : isSelfManaged 
                      ? 'bg-green-500/10 text-green-600'
                      : (isChild || isMentee)
                        ? 'bg-violet-500/10 text-violet-600'
                        : 'bg-muted text-muted-foreground'
                }`}>
                  {isSelfManaged ? 'Self-Managed' : identity.charAt(0).toUpperCase() + identity.slice(1)}
                </span>
              </div>
            </div>
          </DialogHeader>

          {/* Contact Info */}
          <div className="px-6 pb-4 space-y-2">
            {email && (
              <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Email</p>
                  <p className="text-[14px] text-foreground truncate">{email}</p>
                </div>
              </div>
            )}
            {phone && (
              <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Phone</p>
                  <p className="text-[14px] text-foreground">{phone}</p>
                </div>
              </div>
            )}
          </div>

          {/* Supervisor Info (for child/mentee users) */}
          {(isChild || isMentee) && supervisor && (
            <div className="px-6 pb-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mb-2 px-1">
                {isChild ? 'Parent' : 'Mentor'}
              </p>
              <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-xl">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  supervisor.identity === 'mentor' 
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                    : 'bg-gradient-to-br from-violet-500 to-purple-600'
                }`}>
                  {supervisor.identity === 'mentor' ? (
                    <Users className="w-4 h-4 text-white" />
                  ) : (
                    <Shield className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-foreground">
                    {supervisor.firstName} {supervisor.lastName}
                  </p>
                  {supervisor.email && (
                    <p className="text-[12px] text-muted-foreground truncate">{supervisor.email}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Self-Managed Badge */}
          {isSelfManaged && (
            <div className="px-6 pb-4">
              <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-xl border border-green-500/20">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-[14px] font-medium text-foreground">Self-Managed Account</p>
                  <p className="text-[12px] text-muted-foreground">You manage your own device settings</p>
                </div>
              </div>
            </div>
          )}

          {/* Sign Out Button */}
          <div className="px-6 pb-6 pt-2 border-t border-border/50">
            <Button
              variant="outline"
              className="w-full gap-2 h-11"
              onClick={handleSignOut}
              disabled={isSigningOut}
            >
              {isSigningOut ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing out...
                </>
              ) : (
                <>
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
